/**
 * The roguelike layer's data (04-roguelike-layer). Plain JSON-safe records: this is what is
 * saved. Pure: nothing here or in src/meta may touch the DOM, storage or the clock directly.
 */

import type { ArchetypeId, Biome, Gear, IntelId, Inventory, MissionParams, Outcome } from '../sim/types';

export type PerkId =
  | 'steady-hands' | 'controlled-pairs' | 'quick-reload' | 'bunker-buster' | 'snap-shot' | 'deadeye'
  | 'sapper' | 'long-arm' | 'cook-off' | 'mine-sense' | 'wire-cutter' | 'salvager'
  | 'ghost' | 'light-step' | 'knife-work' | 'pathfinder' | 'night-eyes' | 'impersonator'
  | 'iron-lungs' | 'second-wind' | 'field-medic' | 'hard-to-kill' | 'pack-mule'
  | 'cold-blooded' | 'desert-rat' | 'woodsman';

export type TraitId =
  | 'farm-strong' | 'track-star' | 'hunter' | 'swimmer' | 'lucky' | 'hothead' | 'green'
  | 'old-wound' | 'shaken' | 'decorated' | 'tinnitus' | 'blooded';

export type MedalId =
  | 'commendation-1' | 'commendation-2' | 'bronze-star' | 'silver-star' | 'dsc' | 'medal-of-honor'
  | 'purple-heart' | 'pow-medal' | 'evaders-ribbon' | 'pathfinder-badge';

/** The original's ladder, PFC to COL (02 §11); stored as an index 0-11. */
export const RANKS = ['PFC', 'CPL', 'SGT', 'SSG', 'PSG', 'SGM', '2LT', '1LT', 'CPT', 'MAJ', 'LTC', 'COL'] as const;

export type RangerStatus = 'ready' | 'wounded' | 'critical' | 'pow' | 'mia' | 'kia' | 'lost' | 'retired';

export interface ServiceRecord {
  missions: number;
  successes: number;
  kills: number;
  knifeKills: number;
  woundsTaken: number;
  timesCaptured: number;
}

export interface RangerRecord {
  id: string;
  seed: number;
  firstName: string;
  surname: string;
  callsign: string;
  rank: number;
  merit: number;
  perks: PerkId[];
  traits: TraitId[];
  medals: MedalId[];
  record: ServiceRecord;
  status: RangerStatus;
  /** Missions he must sit out before he is `ready` again (04 §4.4). */
  recoveryMissions: number;
  fatigue: number;
  /** A veteran brought in from the Hall of Honor: mortal like everyone else. */
  veteran: boolean;
  /** A perk choice is owed from a promotion not yet resolved. */
  pendingPerkChoices: PerkId[][];
  /** Traits with a countdown (e.g. `shaken` for 2 missions): trait id -> missions left. */
  traitTimers: Partial<Record<TraitId, number>>;
}

/**
 * One mission a named ranger flew: the rows his service file is built from (04 §5).
 *
 * `ServiceRecord` counts how MANY, this says which. It is kept per ranger id at the profile level
 * rather than on `RangerRecord`, because a ranger is copied into five different lists (the company,
 * the Hall, the roll of honour, the Classic roster, a memorial snapshot) and a history on the record
 * itself would be duplicated into every one of them - and frozen at death in the memorial's copy.
 */
export interface ServiceEntry {
  /** Operation codename, or 'CLASSIC'. */
  operation: string;
  /** As the debrief worded it: "Destroy a Munitions Depot (Arctic)". */
  mission: string;
  archetype: ArchetypeId;
  threat: number;
  outcome: Outcome;
  /** He did what he was sent to do, whether or not he came home. */
  objectiveComplete: boolean;
  score: number;
  kills: number;
  /** Wounds he was carrying at the end. */
  wounds: number;
  /** Won on THIS mission: the provenance a bare `RangerRecord.medals` cannot give. */
  medals: MedalId[];
  /** The rank this mission promoted him to, if it did. */
  promotedTo?: number;
}

export interface MemorialEntry {
  ranger: RangerRecord;
  fate: 'kia' | 'lost';
  operation: string;
  mission: string;
  cause: string;
  epitaph: string;
  /** His tags were recovered by a later mission: his medals are confirmed (04 §2.3). */
  tagsRecovered?: boolean;
  /**
   * PHOTOS: the id of the picture taken as he fell (src/platform/photos), kept on the device and synced under the
   * save code. Absent for the lost, for the dead of older saves, and wherever no picture could be taken.
   */
  photo?: string;
}

export type NodeKind = 'mission' | 'rest' | 'supply' | 'depot' | 'intercept' | 'rescue';

export interface TheatreNode {
  id: string;
  theatre: number;
  column: number;
  row: number;
  kind: NodeKind;
  /** Mission and rescue nodes. */
  params: MissionParams | null;
  keystone: boolean;
  /** Tag Recovery (04 §2.3): a fallen ranger's marker will stand on this mission's map. */
  tagRecovery?: { name: string };
  /** Non-native archetype/terrain pairing (03 §2.1). */
  variant: boolean;
  rewardRp: number;
  rewardMultiplier: number;
  /** Ids of nodes in the next column this one leads to. */
  links: string[];
  state: 'hidden' | 'scouted' | 'open' | 'done' | 'failed' | 'skipped';
}

export type ArmoryItem = keyof Inventory | 'smoke' | 'nvg' | 'wire-cutters' | 'suppressed-pistol' | 'ghillie';

export interface Operation {
  seed: number;
  codename: string;
  /** 0 Milk Run, 1 Standard, 2 Hard, 3 Suicide (02 §12). */
  difficulty: number;
  hazard: number;
  theatres: Biome[];
  nodes: TheatreNode[];
  /** Id of the node the company stands on; null before the first choice. */
  position: string | null;
  company: RangerRecord[];
  rp: number;
  /** Enemy Readiness 0-10 (04 §2.3). */
  readiness: number;
  keystoneFailures: number;
  /** Perks recovered tags have made teachable, one ranger each (04 §2.3). */
  trainablePerks?: PerkId[];
  status: 'active' | 'won' | 'lost' | 'withdrawn';
  /** Human-readable log of what happened, newest last. */
  log: string[];
  cadet: boolean;
}

export interface Profile {
  version: number;
  commendations: number;
  unlocks: string[];
  /** Up to six living veterans (04 §5). */
  hall: RangerRecord[];
  retired: RangerRecord[];
  memorial: MemorialEntry[];
  operation: Operation | null;
  /** Classic mode's persistent six-slot roster. */
  classicRoster: RangerRecord[];
  bestScores: Partial<Record<ArchetypeId, number>>;
  operationsWon: number;
  hazardUnlocked: number;
  /**
   * Every mission each ranger has flown, by his id, oldest first (`ServiceEntry`). Kept for anyone
   * the profile still knows - the company, the Hall, the roll of honour, the Classic roster and the
   * wall - so a dead man's file reads exactly as it did the day he died.
   */
  flights: Record<string, ServiceEntry[]>;
}

/** What the sim reports back about a finished mission, beyond the score. */
export interface MissionSummary {
  outcome: Outcome;
  objectiveComplete: boolean;
  score: number;
  threat: number;
  maxAlert: number;
  woundsAtEnd: number;
  wasDowned: boolean;
  kills: number;
  knifeKills: number;
  captivesSaved: number;
  freedPows: boolean;
  /** Mission clock used, seconds: for the best-time records. */
  secondsUsed?: number;
  /** Tag Recovery: he brought the fallen ranger's tags home (only counts if he got home himself). */
  tagsRecovered?: boolean;
  /** ROUND4: he dropped the span under the convoy (10 §2, mission 13) - a Silver Star deed in its own right. */
  spanDeed?: boolean;
  /**
   * Rare equipment still on him at the end (04 §6). Absent: the goggles, cutters and pistol are
   * assumed to come home with him and every smoke grenade to be spent.
   */
  gearLeft?: Partial<Gear>;
  /** PHOTOS: killed in action, the id of the picture taken as he fell; the memorial entry carries it on. */
  photo?: string;
}

/** What changed because of a mission; the debrief screen walks the player through it. */
export interface Aftermath {
  ranger: RangerRecord;
  meritGained: number;
  rpGained: number;
  promotedTo: number | null;
  perkChoices: PerkId[] | null;
  medals: MedalId[];
  readinessChange: number;
  readinessReasons: string[];
  freedRangers: RangerRecord[];
  memorial: MemorialEntry | null;
  operationStatus: Operation['status'];
  /** Tag Recovery paid off (04 §2.3): whose tags came home, and the perk now teachable (null: he had none). */
  tagRecovery?: { name: string; perk: PerkId | null };
}

/* ------------------------------------------------------------ added for the full game */

/** Rare equipment (04 §6): stocked by the operation, taken through `Loadout.gear`. */
export type SpecialItem = 'smoke' | 'nvg' | 'wire-cutters' | 'suppressed-pistol' | 'ghillie';

/** An intel card on sale for one mission node (04 §5). */
export interface IntelOffer {
  id: IntelId;
  price: number;
  /** Already bought, or held free from a Signals Intercept. */
  owned: boolean;
}

/** One line of the Daily Drop's local table (04 §11). */
export interface DailyEntry {
  /** UTC date, `YYYY-MM-DD`. */
  date: string;
  archetype: ArchetypeId;
  biome: Biome;
  rangerName: string;
  outcome: Outcome;
  score: number;
  secondsUsed: number;
}

export interface DailyStatus {
  date: string;
  params: MissionParams;
  rangerName: string;
  /** One attempt a day: set once it has been flown. */
  played: DailyEntry | null;
}

export interface ArchetypeRecord {
  attempts: number;
  completions: number;
  bestScore: number;
  /** Fastest successful mission, seconds of clock used; null until one succeeds. */
  bestSeconds: number | null;
}

/** Records (04 §9, 08 §2.10): everything is derived from the profile, nothing is stored twice. */
export interface Records {
  byArchetype: Partial<Record<ArchetypeId, ArchetypeRecord>>;
  longestServing: { name: string; missions: number } | null;
  mostDecorated: { name: string; medals: number } | null;
  /** Most consecutive successful missions by one ranger. */
  bestStreak: number;
  operationsWon: number;
  rangersLost: number;
  daily: DailyEntry[];
}
